<template>
  <div class="import-labels-container">

    <h3 class="import-labels-title">IMPORT LANGUAGES</h3>
    <div>
      <div>
        Danger! You are about to perform an action on the system that cannot be
        undone.
      </div>
      <div>
        Your last backup was on:
        <span v-if="backup"> {{ backup.backupDate | formatDateWithTime }}</span>
        <span v-if="!backup">No backups found</span>
      </div>
      <div v-if="!hasABackup()" class="import-labels-backup-now-container">
        To continue, an updated language backup (made in the last 24 hours) must
        exist.
        <h4 class="import-labels-backup-now" @click="goToBackup()">
          BACKUP NOW
        </h4>
      </div>
      <div>
        Before uploading the file, please be sure it complies with the following
        rules: <br />
        CSV FILE REQUIREMENTS <br />
        - Must be a valid CVS file:<br />
        Fields separated by comma (,)<br />
        Fields enclosed in double quotes (“) <br />
        Double quotes correctly escaped (“=“”).<br />
        COLUMN REQUIREMENTS <br />
        - Must have the exact column names: <br />
        languageLabel <br />
        languageTranslation <br />
        languageCode <br />
        DATA REQUIREMENTS <br />
        - No new labels can be imported. The import process will only accept
        labels already created in the system. <br />
        - Although recommended, it's not necessary to include all the labels in
        the system. You can include only the labels you wish to update. <br />
        - It's not recommended to use html or css in line styling in the labels
        unless explicitly prescribed. This can break the presentation and lead
        to unintended consequences. <br />
        Use your best judgement and please be careful. Double check you're
        uploading the correct file to the correct language. No duplicates.
        <br />
        Accented characters are supported and encouraged you must use UTF-16
        encoding. Avoid using typographical and not printable characters (this
        happens specially when copy pasting from word processing
        applications).<br />
        Generally speaking do not try to paste anything other than plain text
        (no tables, no symbols, no emojis, no styling).<br />
      </div>
    </div>

    <b-button
      class="labels-regenerate-regenerate-button"
      variant="primary"
      :disabled="!hasABackup()"
      @click="showModal = true"
    >
      IMPORT LANGUAGE
    </b-button>

    <h4 class="backup-labels-sub-title">EXISTING BACKUPS</h4>

    <b-table :items="logs" :fields="fields" striped>
      <template #cell(cacheDate)="data">
        {{ data.item.cacheDate | formatDateWithTime }}
      </template>
      <template #cell(language)="data">
        {{ langs[data.item.language] }}
      </template>

      <template #cell(actions)="data">
        <b-button
          class="backup-labels-button-outline"
          variant="primary"
          @click="selectBackupToRestore(data.item)"
        >
          Restore
        </b-button>
        <b-button
          class="backup-labels-button-outline"
          variant="primary"
          @click="selectBackupToDelete(data.item)"
        >
          Delete
        </b-button>
      </template>
    </b-table>

    <b-modal
      id="modalInfo"
      v-model="showModal"
      no-close-on-backdrop
      no-close-on-esc
      modal-class="modalInfo"
      title="Import language"
    >
      <div v-if="!errorInFile && !insertedLog && !success">
        <b-form-group label="Select the target language">
          <b-select v-model="selectedLanguage" :options="languages" />
        </b-form-group>
        <b-form-group label="Select the CSV file you want to import">
          <b-form-file v-model="file" />
        </b-form-group>
      </div>
      <div v-if="!errorInFile && insertedLog && !success">
        The import file: {{ insertedLog.originalFileName }} has been
        successfully validated. <br />
        Please note that you are creating {{toCreate}} labels and modifying {{ toUpdate }} labels ({{
          ((toUpdate / total) * 100).toFixed(2)
        }}%) of total translation<br />
        <b-checkbox v-model="confirmation">
          I confirm that I understand this action will overwrite this language
          in the system.
        </b-checkbox>
        <br />
        If you are sure you want to overwrite the language in the system, please
        write the word YES in the field below:
        <b-input v-model="yesInput" />
      </div>
      <div v-if="!errorInFile && success && insertedLog">
        The language: {{ insertedLog.language }} has been updated with the file:
        {{ insertedLog.originalFileName }}<br />
        We strongly recommend you review the changes in the “Translation Tool”
        to make sure they are correct, then go live when ready.
        <br />If you made a mistake please roll back using the RESTORE feature
        in the Backup - Restore Language section.
      </div>
      <div v-if="errorInFile">
        The import file failed validated.
        <br />
        Please correct the following problems and try again <br />
        <div class="import-labels-problems">
          - Invalid file format. Must export to CSV. <br />
          - Must have the required column names. <br />
          - Must have the required data columns. <br />
          - languageLabel doesn’t exist in the system. <br />
          - Invalid characters used. Please use UTF-16 encoding. <br />
          - Duplicated language label
        </div>
      </div>
      <div slot="modal-footer" class="import-labels-modal-footer">
        <div v-if="!errorInFile && !insertedLog && !success">
          <b-button @click="showModal = false"> Close </b-button>
          <b-button variant="primary" :disabled="!file" @click="postCsv()">
            Import
          </b-button>
        </div>
        <div v-if="!errorInFile && insertedLog && !success">
          <b-btn variant="primary" @click="showModal = false"> Close </b-btn>
          <b-btn
            variant="primary"
            :disabled="!yesInput || yesInput !== 'YES' || !confirmation"
            @click="acceptUpload()"
          >
            Upload file and overwrite language
          </b-btn>
        </div>
        <div v-if="!errorInFile && success">
          <b-btn variant="primary" @click="clearModal()"> Close </b-btn>
        </div>
        <div v-if="errorInFile">
          <b-btn variant="primary" @click="clearModal()"> Close </b-btn>
        </div>
      </div>
    </b-modal>
  </div>
</template>


<script>
import Vue from "vue";
import Icon from "vue-awesome/components/Icon";
import "vue-awesome/icons";
import "@/assets/icons/icons";
import { axiosCall } from "../../../../utils/axiosCall";

Vue.component("Icon", Icon);

export default {
  name: "LabelsExport",
  data: function () {
    return {
      showModal: false,
      languages: [],

      selectedLanguage: null,
      backup: {},
      langs: {},
      file: null,
      fields: ["cacheDate", "language", "originalFileName"],
      toUpdate: 0,
      toCreate: 0,
      total: 0,
      insertedLog: null,
      confirmation: false,
      yesInput: null,
      success: false,
      errorInFile: false,
      logs: [],
    };
  },
  created: async function () {
    await this.getLangs();
    await this.getAllBackups();
    await this.getLogs();
    await this.getAvailableLangs();
  },
  methods: {
    getAllBackups: async function () {
      await axiosCall({
        url: "LabelBackups/GetLastBackup",
        method: "GET",
      }).then(async (response) => {
        this.backup = response.data;
      });
    },
    hasABackup() {
      if (this.backup && this.backup.backupDate) {
        var d = new Date();
        d.setHours(d.getHours() - 24);
        return new Date(this.backup.backupDate).getTime() > d.getTime();
      }
      return false;
    },
    getAvailableLangs: async function () {
      await axiosCall({ url: "/Langs/AvailableLanguages", method: "GET" }).then(
        async (response) => {
          response.data.map((language) => {
            this.langs[language.id] = language.language;
          });
        }
      );
    },
    getLangs: async function () {
      await axiosCall({ url: "/Langs/AvailableLanguages", method: "GET" }).then(
        async (response) => {
          this.languages = response.data.map((avL) => {
            return {
              text: avL.language,
              value: avL.id,
            };
          });
          if (this.languages && this.languages.length) {
            this.selectedLanguage = this.languages[0].value;
          }
        }
      );
    },
    getLogs: async function () {
      await axiosCall({ url: "LabelImport/log", method: "GET" }).then(
        async (response) => {
          this.logs = response.data;
        }
      );
    },
    goToBackup() {
      this.$router.push("/LabelsBackup");
    },
    postCsv: async function () {
      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("test", "testValue");
      await axiosCall({
        url: `/LabelImport/csv/${this.selectedLanguage}`,
        method: "POST",
        headers: { "content-type": "multipart/form-data" },
        data: formData,
      }).then(async (response) => {
        if (response.data) {
          this.insertedLog = response.data.insertedLog;
          this.toUpdate = response.data.toUpdate;
          this.toCreate = response.data.toCreate;
          this.total = response.data.total;
        } else {
          this.errorInFile = true;
        }
      });
    },
    acceptUpload: async function () {
      await axiosCall({
        url: `/LabelImport/Accept/${this.insertedLog.id}`,
        method: "GET",
      }).then(async () => {
        this.success = true;
      });
    },
    clearModal: function () {
      this.showModal = false;
      this.insertedLog = null;
      this.success = false;
      this.errorInFile = false;
    },
  },
};
</script>
<style lang="scss">
.import-labels {
  &-container {
    padding: 30px 60px;
  }

  &-title {
    margin-bottom: 15px;
  }

  &-regenerate-button {
    padding: 15px;
    margin-top: 20px;
    margin-bottom: 20px;
    border-radius: 5px !important;
  }

  &-last-time {
    font-size: 0.8rem;
  }

  &-backup-now {
    text-decoration: underline;
    cursor: pointer;
    display: inline-block;
    margin-left: 7px;

    &-container {
      color: #a70404;
    }
  }

  &-problems {
    color: #a70404;
  }

  &-modal {
    &-footer {
      justify-content: flex-end !important;

      .btn {
        margin-left: 10px;
        border-radius: 5px !important;
      }
    }
  }
}
</style>
